import {
  Container,
  Box,
  Grid,
  SpaceBetween,
  Button,
  GridProps,
  Modal,
  Link,
  Header,
  BarChart,
  Spinner,
  StatusIndicator,
} from '@cloudscape-design/components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import 'react-lazy-load-image-component/src/effects/blur.css';
import {
  ChatMessage,
  GraphMessage,
  LoadingMessage,
  PieChartMessage,
  TableMessage,
  TextMessage,
  ValueMessage,
} from '../common/chat/types';

import { CenteredComparison, CenteredTextComparison } from './reporting';
import { ChatGraph } from './graph';
import { ChatTable } from './table';
import { ChatPieChart } from './pie';

export type ChatTurnProps = {
  index: number;
  length: number;
  message: ChatMessage;
  addMessage?: (message: string) => void;
  loading?: boolean;
};

export function ChatTurn(props: ChatTurnProps) {
  const message = props.message;
  const index = props.index;

  let content = null;

  if (message.type === 'text') {
    const textMessage = message as TextMessage;

    const textContent =
      index === 0 ? (
        <h2 className="text-xl font-bold">{message.message}</h2>
      ) : (
        <div className="space-y-2">
          <ReactMarkdown
            children={message.message}
            className="prose prose-sm md:prose-lg lg:prose-xl"
            components={{
              ul: ({ node, ...props }) => <ul className="list-disc list-inside pl-2" {...props} />,
              ol: ({ node, ...props }) => (
                <ol className="list-decimal list-inside pl-2" {...props} />
              ),
              li: ({ node, ...props }) => <li className="mb-2 space-y-4" {...props} />,
              div: ({ node, ...props }) => <div {...props} className="space-y-4" />,
              a: ({ node, ...props }) => (
                <a
                  className="text-blue-400 hover:text-blue-300"
                  href={props.href as string}
                  rel="noreferrer"
                  target="_blank"
                >
                  {props.children}
                </a>
              ),
            }}
            remarkPlugins={[remarkGfm]}
          />
        </div>
      );

    if (textMessage.options && props.addMessage && index === props.length - 1) {
      const options = textMessage.options.map((option, option_idx) => (
        <button
          key={`turn-${index}-option-${option_idx}`}
          className={`text-white p-4 rounded-lg shadow-lg transition-transform transform hover:scale-105 ${
            props.loading ? 'cursor-not-allowed bg-gray-800' : 'cursor-pointer bg-slate-950'
          }`}
          disabled={props.loading}
          onClick={() => {
            if (props.addMessage && !props.loading) {
              props.addMessage(option.value);
            }
          }}
        >
          <h3 className="text-lg font-semibold">{option.title}</h3>
          <p className="text-sm text-gray-300">{option.subtitle}</p>
        </button>
      ));

      content = (
        <div className="chat-options space-y-4">
          {textContent}
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">{options}</div>
        </div>
      );
    } else {
      content = textContent;
    }
  } else if (message.type === 'graph') {
    const graphMessage = message as GraphMessage;
    // console.log(graphMessage);

    content = <ChatGraph message={graphMessage} />;
  } else if (message.type === 'table') {
    const tableMessage = message as TableMessage;
    // console.log(tableMessage);

    content = <ChatTable message={tableMessage} />;
  } else if (message.type === 'value') {
    const valueMessage = message as ValueMessage;

    if (valueMessage.value_type === 'currency' || valueMessage.value_type === 'number') {
      content = (
        <CenteredComparison
          description={valueMessage.subtitle}
          formatter={
            valueMessage.value_type === 'number'
              ? (x) =>
                  x.toLocaleString(undefined, {
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0,
                  })
              : undefined
          }
          previousValue={valueMessage.prev_value as number | undefined}
          value={valueMessage.value as number}
          variant="percentage" // TODO let the AI choose if it should be a percentage or absolute difference
          insideContainer
        >
          {valueMessage.title}
        </CenteredComparison>
      );
    } else if (valueMessage.value_type === 'datetime') {
      content = (
        <CenteredTextComparison
          description={valueMessage.subtitle}
          previousValue={
            valueMessage.prev_value
              ? (new Date((valueMessage.prev_value as string) + 'Z').toLocaleString() as
                  | string
                  | undefined)
              : undefined
          }
          value={new Date((valueMessage.value as string) + 'Z').toLocaleString()}
          insideContainer
        >
          {valueMessage.title}
        </CenteredTextComparison>
      );
    } else if (valueMessage.value_type === 'string') {
      content = (
        <CenteredTextComparison
          description={valueMessage.subtitle}
          previousValue={valueMessage.prev_value as string | undefined}
          value={valueMessage.value as string}
          insideContainer
        >
          {valueMessage.title}
        </CenteredTextComparison>
      );
    }
  } else if (message.type === 'loading') {
    const loadingMessage = message as LoadingMessage;

    if (loadingMessage.error) {
      let text = '';

      if (loadingMessage.name === 'show_graph') {
        text = 'Graph error';
      } else if (loadingMessage.name === 'show_table') {
        text = 'Table error';
      } else if (loadingMessage.name === 'run_sql') {
        text = 'Data error';
      } else if (loadingMessage.name === 'show_value') {
        text = 'Value error';
      }
      content = (
        <Box variant="p">
          <StatusIndicator type="error">{loadingMessage.name ? text : ''}</StatusIndicator>
        </Box>
      );
    } else if (loadingMessage.complete) {
      let text = '';

      if (loadingMessage.name === 'show_graph') {
        text = 'Graph built';
      } else if (loadingMessage.name === 'show_table') {
        text = 'Table built';
      } else if (loadingMessage.name === 'run_sql') {
        text = 'Data loaded';
      } else if (loadingMessage.name === 'show_value') {
        text = 'Value loaded';
      }
      content = (
        <Box variant="p">
          <StatusIndicator type="success">{loadingMessage.name ? text : ''}</StatusIndicator>
        </Box>
      );
    } else {
      let text = '';

      if (loadingMessage.name === 'show_graph') {
        text = 'Building graph...';
      } else if (loadingMessage.name === 'show_table') {
        text = 'Building table...';
      } else if (loadingMessage.name === 'run_sql') {
        text = 'Loading data...';
      } else if (loadingMessage.name === 'show_value') {
        text = 'Loading value...';
      }

      content = (
        <Box variant="p">
          {/* TODO friendly name and icon here */}
          <Spinner size="normal" /> {loadingMessage.name ? text : ''}
        </Box>
      );
    }
  } else if (message.type === 'pie') {
    const pieMessage = message as PieChartMessage;
    // console.log(pieMessage);
    content = <ChatPieChart message={pieMessage} />;
  } else {
    console.error('Unknown message type: ', message);
  }

  return (
    <div
      key={props.index}
      className={`message py-2 max-w-auto break-words mr-2 ${
        message.role === 'user'
          ? 'bg-gray-700 text-white self-end rounded-tl-lg rounded-bl-lg rounded-tr-lg custom-overflow-wrap px-4'
          : 'self-start rounded-tl-lg rounded-tr-lg rounded-br-lg px-2'
      }`}
    >
      <div className="text-xs text-gray-500 mb-1">{message.role === 'user' ? 'You' : 'AI'}</div>
      {content}
    </div>
  );
}
